import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { logout, getUserPermissions, getUserRole } from "../../../shared/lib/auth/authHelper";
import { useTranslation } from "react-i18next";
import styles from "../../../styles/menu.module.css";
import LanguageSwitcherSideBarMenu from "../../../components/LanguageSwitcherSideBarMenu";
import { ChatProvider, useChat } from "../../chat/context/ChatContext";
import FloatingChatLauncher from "../../chat/components/FloatingChatLauncher";
import { useTenantBranding } from "../../../shared/branding/TenantBrandingProvider";
import api from "../../../shared/api/apiClient";
import { AcademyProvider } from "../../academy/AcademyContext";


const DEFAULT_FEATURE_VISIBILITY = {
  dashboard: true,
  products: true,
  employees: true,
  chat: true,
  companies: true,
  contacts: true,
  contact_import: true,
  leads: true,
  deals: true,
  tasks: true,
  sources: true,
  pipeline: true,
  activities: true,
  logs: true,
  settings_products: true,
  crm_settings: true,
  shop_settings: true,
  tenant_control: true,
  permissions: true,
  external_api: true,
  telegram_bot: true,
  marketing: true,
  academy: true,
  fleet_control: false,
  documents_invoices: false,
};

const featureEnabled = (featureVisibility, key) => {
  const map =
    featureVisibility && typeof featureVisibility === "object"
      ? featureVisibility
      : DEFAULT_FEATURE_VISIBILITY;

  return map?.[key] !== false;
};

const hasPermission = (permissions, code) =>
  Array.isArray(permissions) && permissions.includes(code);

const hasAnyPermission = (permissions, codes) =>
  Array.isArray(permissions) &&
  codes.some((code) => permissions.includes(code));

const withVisibleChildren = (item) => {
  if (!item?.children) return item;

  const children = item.children.filter(Boolean);

  if (children.length === 0) return null;

  return { ...item, children };
};

const canSeePermissionsPage = (permissions) =>
  hasAnyPermission(permissions, [
    "permissions.view",
    "roles.view",
    "role_permissions.view",
  ]);

const getLocalTaskClock = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return {
    today: local.toISOString().slice(0, 10),
    now: local.toISOString().slice(0, 19),
  };
};

const buildNavItems = (
  t,
  permissions,
  branding,
  featureVisibility,
  overdueTaskCount,
  isSuperAdmin,
) =>
  [
    featureEnabled(featureVisibility, "academy")
      ? {
          key: "academy",
          label: t("academy", { defaultValue: "Academy" }),
          icon: "lni lni-graduation-cap-1",
          route: "/menu/academy",
          mobile: true,
        }
      : null,

    hasPermission(permissions, "dashboard.view") && featureEnabled(featureVisibility, "dashboard")
      ? {
          key: "dashboard",
          label: t("dashboard"),
          icon: "lni lni-dashboard-square-1",
          route: "/menu/dashboard",
          mobile: true,
        }
      : null,

    hasPermission(permissions, "products.view") && featureEnabled(featureVisibility, "products")
      ? {
          key: "products",
          label: t("orders"),
          icon: "lni lni-line-height",
          route: "/menu/products",
          mobile: true,
        }
      : null,

    (isSuperAdmin || (hasAnyPermission(permissions, ["users.view", "chat.view"]) && (featureEnabled(featureVisibility, "employees") || featureEnabled(featureVisibility, "chat"))))
      ? withVisibleChildren({
          key: "users",
          label: t("users"),
          icon: "lni lni-user-multiple-4",
          mobile: true,
          children: [
            (isSuperAdmin || (hasPermission(permissions, "users.view") && featureEnabled(featureVisibility, "employees")))
              ? {
                  key: "employees",
                  label: t("employees"),
                  route: "/menu/employees",
                }
              : null,

            hasPermission(permissions, "chat.view") && featureEnabled(featureVisibility, "chat")
              ? {
                  key: "chat",
                  label: t("chat"),
                  icon: "lni lni-comments-2",
                  route: "/menu/chat",
                }
              : null,
          ],
        })
      : null,

    hasAnyPermission(permissions, [
      "companies.view",
      "contacts.view",
      "contacts.import",
      "leads.view",
      "deals.view",
      "tasks.view",
      "sources.view",
      "activities.view",
      "pipeline.view",
    ])
      ? withVisibleChildren({
          key: "sales",
          label: t("CRM"),
          icon: "lni lni-briefcase-1",
          mobile: true,
          children: [
            hasPermission(permissions, "companies.view") && featureEnabled(featureVisibility, "companies")
              ? {
                  key: "potential-clients",
                  label: t("potential_companies"),
                  route: "/menu/potential-clients",
                }
              : null,

            hasPermission(permissions, "contacts.view") && featureEnabled(featureVisibility, "contacts")
              ? {
                  key: "sales-contacts",
                  label: t("sales_contacts"),
                  route: "/menu/sales/contacts",
                }
              : null,

            hasPermission(permissions, "leads.view") && featureEnabled(featureVisibility, "leads")
              ? {
                  key: "sales-leads",
                  label: t("sales_leads"),
                  route: "/menu/sales/leads",
                }
              : null,

            hasPermission(permissions, "deals.view") && featureEnabled(featureVisibility, "deals")
              ? {
                  key: "sales-deals",
                  label: t("sales_deals"),
                  route: "/menu/sales/deals",
                }
              : null,

            hasPermission(permissions, "tasks.view") && featureEnabled(featureVisibility, "tasks")
              ? {
                  key: "sales-tasks",
                  label: t("sales_tasks"),
                  route: "/menu/sales/tasks",
                  badgeCount: overdueTaskCount,
                }
              : null,

            hasPermission(permissions, "activities.view") && featureEnabled(featureVisibility, "activities")
              ? {
                  key: "sales-activities",
                  label: t("sales_activities"),
                  route: "/menu/sales/activities",
                }
              : null,
          ],
        })
      : null,

    featureEnabled(featureVisibility, "fleet_control")
      ? { key: "fleet-control", label: t("fleet_control", { defaultValue: "Fleet Control" }), icon: "lni lni-car", route: "/menu/fleet", mobile: true }
      : null,

    featureEnabled(featureVisibility, "documents_invoices")
      ? { key: "documents-invoices", label: t("documents_invoices", { defaultValue: "Documents & Invoices" }), icon: "lni lni-files", route: "/menu/invoices", mobile: true }
      : null,

    featureEnabled(featureVisibility, "documents_invoices")
      ? { key: "invoice-analytics", label: t("invoice_analytics_menu", { defaultValue: "Аналитика счетов" }), icon: "lni lni-bar-chart", route: "/menu/invoice-analytics", mobile: true }
      : null,

    hasPermission(permissions, "marketing.view") && featureEnabled(featureVisibility, "marketing")
      ? {
          key: "marketing",
          label: t("marketing", { defaultValue: "Marketing" }),
          icon: "lni lni-target-user",
          route: "/menu/marketing",
          mobile: true,
        }
      : null,

    hasPermission(permissions, "activity_logs.view") && featureEnabled(featureVisibility, "logs")
      ? {
          key: "logs",
          label: t("logs"),
          icon: "lni lni-megaphone-1",
          route: "/menu/logs",
          mobile: true,
        }
      : null,

    hasPermission(permissions, "work_schedule.view")
      ? {
          key: "work-schedule",
          label: t("work_schedule.menu_label", { defaultValue: "Work schedule" }),
          icon: "lni lni-calendar-days",
          route: "/menu/work-schedule",
          mobile: true,
        }
      : null,

    (isSuperAdmin || hasAnyPermission(permissions, [
      "categories.view",
      "sub_categories.view",
      "products.view",
      "crm_settings.view",
      "permissions.view",
      "permissions.assign",
      "roles.view",
      "roles.create",
      "role_permissions.view",
      "role_permissions.assign",
      "role_permissions.unassign",
    ]))
      ? withVisibleChildren({
          key: "settings",
          label: t("setting"),
          icon: "lni lni-code-1",
          mobile: true,
          children: [
            hasAnyPermission(permissions, [
              "categories.view",
              "sub_categories.view",
              "products.view",
            ]) && featureEnabled(featureVisibility, "settings_products")
              ? {
                  key: "settings-products",
                  label: t("SettingsProducts"),
                  route: "/menu/settings/products",
                }
              : null,

            hasPermission(permissions, "crm_settings.view") && featureEnabled(featureVisibility, "crm_settings")
              ? {
                key: "crm-settings",
                label: t("crm_settings"),
                route: "/menu/settings/crm",
              }
              : null,

            hasPermission(permissions, "crm_settings.view") && featureEnabled(featureVisibility, "shop_settings")
              ? {
                key: "shop-settings",
                label: t("shop_settings", { defaultValue: "Интернет-магазин" }),
                route: "/menu/settings/shop",
              }
              : null,

            hasPermission(permissions, "crm_settings.view") && featureEnabled(featureVisibility, "telegram_bot")
              ? {
                  key: "telegram-bot-settings",
                  label: t("telegram_bot", { defaultValue: "Telegram Bot" }),
                  route: "/menu/settings/telegram",
                }
              : null,

            hasPermission(permissions, "crm_settings.view") && featureEnabled(featureVisibility, "telegram_bot")
              ? {
                  key: "telegram-connections-settings",
                  label: t("telegram_connections_settings_card_title", {
                    defaultValue: "Telegram connections",
                  }),
                  route: "/menu/settings/telegram-connections",
                }
              : null,

            branding?.is_main &&
            hasPermission(permissions, "crm_settings.view") && featureEnabled(featureVisibility, "crm_settings")
              ? {
                  key: "tenant-control",
                  label: t("tenant_control", {
                    defaultValue: "Tenant Control",
                  }),
                  route: "/menu/settings/tenants",
                }
              : null,

            (isSuperAdmin || (canSeePermissionsPage(permissions) && featureEnabled(featureVisibility, "permissions")))
              ? {
                  key: "settings-permissions",
                  label: t("permissions"),
                  route: "/menu/settings/permissions",
                }
              : null,
          ],
        })
      : null,
  ].filter(Boolean);

function MenuContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount } = useChat();
  const { branding } = useTenantBranding();
  const isSuperAdmin = String(getUserRole() || "").toLowerCase() === "superadmin";

  const [featureVisibility, setFeatureVisibility] = useState(
    () => ({
      ...DEFAULT_FEATURE_VISIBILITY,
      academy: false,
    })
  );
  const [featureVisibilityLoaded, setFeatureVisibilityLoaded] = useState(false);

  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem("sidebar-expanded");

    if (saved === "0") return false;
    if (saved === "1") return true;

    return true;
  });

  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileDrawer, setMobileDrawer] = useState(null);
  const [permissions, setPermissions] = useState(() =>
    getUserPermissions()
  );
  const [overdueTaskCount, setOverdueTaskCount] = useState(0);

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= 768
  );

  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("osaco-theme");

    if (saved === "dark" || saved === "light") {
      return saved;
    }

    return window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  useEffect(() => {
    localStorage.setItem("osaco-theme", theme);

    document.body.classList.toggle("dark", theme === "dark");
    document.body.classList.toggle("light", theme === "light");

    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;

      setIsMobile(mobile);

      if (mobile) {
        setIsExpanded(false);
      } else {
        setIsExpanded(true);
      }
    };

    onResize();

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (!hasPermission(permissions, "tasks.view")) {
      setOverdueTaskCount(0);
      return undefined;
    }

    let cancelled = false;

    const loadOverdueTaskCount = async () => {
      if (document.visibilityState === "hidden") return;

      try {
        const clock = getLocalTaskClock();
        const { data } = await api.get("/tasks/grouped", {
          params: {
            group: "overdue",
            today: clock.today,
            now: clock.now,
            skip: 0,
            limit: 1,
          },
        });

        if (!cancelled) {
          setOverdueTaskCount(
            Math.max(0, Number(data?.counts?.overdue || 0)),
          );
        }
      } catch {
        // The navigation remains usable when the optional counter is offline.
      }
    };

    const handleTaskChange = () => loadOverdueTaskCount();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") loadOverdueTaskCount();
    };

    loadOverdueTaskCount();
    const intervalId = window.setInterval(loadOverdueTaskCount, 300_000);
    window.addEventListener("velsoft:tasks-changed", handleTaskChange);
    window.addEventListener("online", handleTaskChange);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("velsoft:tasks-changed", handleTaskChange);
      window.removeEventListener("online", handleTaskChange);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [permissions]);

  useEffect(() => {
    localStorage.setItem(
      "sidebar-expanded",
      isExpanded ? "1" : "0"
    );

    if (isExpanded) {
      document.body.classList.remove("sidebar-collapsed");
      document.body.setAttribute("data-sidebar", "expanded");
    } else {
      document.body.classList.add("sidebar-collapsed");
      document.body.setAttribute("data-sidebar", "collapsed");
    }

    window.dispatchEvent(
      new CustomEvent("sidebar-state-changed", {
        detail: { expanded: isExpanded },
      })
    );
  }, [isExpanded]);

  useEffect(() => {
    const onStorage = (e) => {
      if (
        e.key === "osaco-theme" &&
        (e.newValue === "dark" || e.newValue === "light")
      ) {
        setTheme(e.newValue);
      }

      if (e.key === "user_permissions") {
        setPermissions(getUserPermissions());
      }
    };

    window.addEventListener("storage", onStorage);

    return () =>
      window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const syncPermissions = () =>
      setPermissions(getUserPermissions());

    window.addEventListener(
      "token-changed",
      syncPermissions
    );

    window.addEventListener(
      "permissions-updated",
      syncPermissions
    );

    return () => {
      window.removeEventListener(
        "token-changed",
        syncPermissions
      );

      window.removeEventListener(
        "permissions-updated",
        syncPermissions
      );
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get("/auth/crmSettings");
        if (cancelled) return;

        const next =
          data?.settings_json?.feature_visibility &&
          typeof data.settings_json.feature_visibility === "object"
            ? {
                ...DEFAULT_FEATURE_VISIBILITY,
                ...data.settings_json.feature_visibility,
              }
            : DEFAULT_FEATURE_VISIBILITY;

        setFeatureVisibility(next);
        setFeatureVisibilityLoaded(true);
      } catch (e) {
        if (!cancelled) {
          setFeatureVisibility(DEFAULT_FEATURE_VISIBILITY);
          setFeatureVisibilityLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleFeatureVisibilityChanged = (event) => {
      const incoming = event?.detail?.feature_visibility;
      if (!incoming || typeof incoming !== "object") return;

      setFeatureVisibility({
        ...DEFAULT_FEATURE_VISIBILITY,
        ...incoming,
      });
      setFeatureVisibilityLoaded(true);
    };

    window.addEventListener(
      "velsoft:feature-visibility-changed",
      handleFeatureVisibilityChanged,
    );

    return () => {
      window.removeEventListener(
        "velsoft:feature-visibility-changed",
        handleFeatureVisibilityChanged,
      );
    };
  }, []);

  const academyEnabled =
    featureVisibilityLoaded && featureEnabled(featureVisibility, "academy");
  const retailShopEnabled =
    featureVisibilityLoaded && featureEnabled(featureVisibility, "shop_settings");

  useEffect(() => {
    if (
      featureVisibilityLoaded &&
      !academyEnabled &&
      (location.pathname === "/menu/academy" ||
        location.pathname.startsWith("/menu/academy/"))
    ) {
      navigate("/menu/dashboard", { replace: true });
    }
  }, [
    academyEnabled,
    featureVisibilityLoaded,
    location.pathname,
    navigate,
  ]);


  useEffect(() => {
    if (
      featureVisibilityLoaded &&
      !retailShopEnabled &&
      (location.pathname === "/menu/settings/shop" ||
        location.pathname.startsWith("/menu/settings/shop/"))
    ) {
      navigate("/menu/settings/crm", { replace: true });
    }
  }, [retailShopEnabled, featureVisibilityLoaded, location.pathname, navigate]);

  const items = useMemo(
    () =>
      buildNavItems(
        t,
        permissions,
        branding,
        featureVisibility,
        overdueTaskCount,
        isSuperAdmin,
      ),
    [t, permissions, branding, featureVisibility, overdueTaskCount]
  );

  useEffect(() => {
    const activeParent = items.find(
      (item) =>
        item.children &&
        item.children.some(
          (sub) =>
            sub.route &&
            (location.pathname === sub.route ||
              location.pathname.startsWith(sub.route))
        )
    );

    setOpenDropdown(activeParent ? activeParent.key : null);
  }, [location.pathname, items]);

  const toggleDropdown = (key) => {
    setOpenDropdown((prev) =>
      prev === key ? null : key
    );
  };

  const toggleSidebar = () => {
    setIsExpanded((p) => !p);
  };

  const toggleTheme = () => {
    setTheme((prev) =>
      prev === "dark" ? "light" : "dark"
    );
  };

  const handleItemClick = (route) => {
    if (route) {
      navigate(route);
    }

    if (window.innerWidth <= 768) {
      setIsExpanded(false);
    }
  };

  const wrapperClass = [
    styles.wrapper,
    isExpanded ? styles.expanded : styles.collapsed,
    theme === "dark" ? styles.dark : styles.light,
  ].join(" ");

  const isActive = (route) =>
    route &&
    (location.pathname === route ||
      location.pathname.startsWith(route));

  // FIXED
  const mobileNavItems = items;

  const activeMobileChildren = mobileDrawer
    ? items.find((x) => x.key === mobileDrawer)
        ?.children || []
    : [];

  return (
    <AcademyProvider enabled={academyEnabled}>
      <div className={wrapperClass}>
      {isMobile && isExpanded && (
        <div
          className={styles.mobileOverlay}
          onClick={() => setIsExpanded(false)}
        />
      )}

      <aside
        className={`${styles.sidebar} ${
          isMobile && isExpanded
            ? styles.mobileSidebarOpen
            : ""
        }`}
      >
        <div className={styles.headerRow}>
          <button
            className={styles.toggleBtn}
            type="button"
            onClick={toggleSidebar}
          >
            <i className="lni lni-menu-hamburger-1"></i>
          </button>

          {isExpanded && (
            <div className={styles.mobileBrand}>
              <div className={styles.mobileBrandTitle}>
                {branding?.company_name || "CRM"}
              </div>

              <div className={styles.mobileBrandSubtitle}>
                {t("crm_business_platform")}
              </div>
            </div>
          )}
        </div>

        <ul className={styles.nav}>
          {items.map((item) => {
            const showBadge =
              item.key === "chat" && unreadCount > 0;

            return (
              <li key={item.key} className={styles.navItem}>
                <button
                  className={`${styles.navLink} ${
                    isActive(item.route)
                      ? styles.active
                      : ""
                  }`}
                  onClick={() =>
                    item.children
                      ? toggleDropdown(item.key)
                      : handleItemClick(item.route)
                  }
                  type="button"
                >
                  <i className={item.icon || "lni lni-dot"} />

                  {isExpanded && (
                    <>
                      <span>{item.label}</span>

                      {showBadge && (
                        <div className={styles.chatBadge}>
                          {unreadCount > 99
                            ? "99+"
                            : unreadCount}
                        </div>
                      )}

                      {!!item.children && (
                        <i
                          className={`lni lni-chevron-right ${
                            styles.chevron
                          } ${
                            openDropdown === item.key
                              ? styles.rotated
                              : ""
                          }`}
                        />
                      )}
                    </>
                  )}
                </button>

                {!!item.children && (
                  <ul
                    className={`${styles.subnav} ${
                      openDropdown === item.key
                        ? styles.open
                        : ""
                    }`}
                  >
                    {item.children.map((sub) => (
                      <li key={sub.key}>
                        <button
                          className={`${styles.subLink} ${
                            isActive(sub.route)
                              ? styles.active
                              : ""
                          }`}
                          onClick={() =>
                            handleItemClick(sub.route)
                          }
                          type="button"
                        >
                          <i
                            className={
                              sub.icon || "lni lni-dot"
                            }
                          />

                          <span>{sub.label}</span>

                          {Number(sub.badgeCount || 0) > 0 && (
                            <span
                              className={styles.priorityBadge}
                              title={t("overdue_tasks_count", { count: sub.badgeCount })}
                              aria-label={t("overdue_tasks_count", { count: sub.badgeCount })}
                            >
                              {sub.badgeCount > 99 ? "99+" : sub.badgeCount}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        <div className={styles.footer}>
          <button
            className={`${styles.navLink} ${
              isActive("/menu/account")
                ? styles.active
                : ""
            }`}
            onClick={() =>
              handleItemClick("/menu/account")
            }
            type="button"
          >
            <i className="lni lni-user-4" />

            {isExpanded && (
              <span>{t("my_account")}</span>
            )}
          </button>

          <button
            className={styles.logout}
            onClick={() => {
              logout();
              navigate("/");
            }}
            type="button"
          >
            <i className="lni lni-exit" />

            {isExpanded && <span>{t("logout")}</span>}
          </button>

          {isExpanded && (
            <>
              <div className={styles.themeRow}>
                <span className={styles.themeLabel}>
                  {theme === "light"
                    ? t("light_mode")
                    : t("dark_mode")}
                </span>

                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={theme === "light"}
                    onChange={toggleTheme}
                  />

                  <span className={styles.slider} />

                  <i
                    className={`lni ${
                      theme === "light"
                        ? "lni-sun-1"
                        : "lni-moon-half-right-5"
                    }`}
                  />
                </label>
              </div>

              <div className={styles.languageRow}>
                <LanguageSwitcherSideBarMenu
                  isExpanded={isExpanded}
                />
              </div>
            </>
          )}
        </div>
      </aside>

      <main className={styles.main}>
        {isMobile && (
          <div className={styles.mobileTopbar}>
            <button
              className={styles.mobileMenuButton}
              onClick={() => setIsExpanded(true)}
            >
              <i className="lni lni-menu-hamburger-1"></i>
            </button>

            <div className={styles.mobileTopbarTitle}>
              {branding?.company_name || "CRM"}
            </div>
          </div>
        )}

        <div className={styles.pageContent}>
          <Outlet />
        </div>
      </main>

      {isMobile && (
        <>
          <nav className={styles.bottomNav}>
            <div className={styles.bottomNavScroll}>
              {mobileNavItems.map((item) => {
                const route =
                  item.route ||
                  item.children?.[0]?.route;

                const active =
                  isActive(route) ||
                  item.children?.some((sub) => isActive(sub.route));

                return (
                  <button
                    key={item.key}
                    className={`${styles.bottomNavItem} ${
                      active
                        ? styles.bottomNavActive
                        : ""
                    }`}
                    onClick={() => {
                      if (item.children?.length) {
                        setMobileDrawer(item.key);
                      } else {
                        navigate(route);
                      }
                    }}
                  >
                    <i className={item.icon} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {!!mobileDrawer && (
            <>
              <div
                className={styles.mobileDrawerBackdrop}
                onClick={() => setMobileDrawer(null)}
              />

              <div className={styles.mobileDrawer}>
                <div className={styles.mobileDrawerHeader}>
                  <div className={styles.mobileDrawerTitle}>
                    {
                      items.find(
                        (x) => x.key === mobileDrawer
                      )?.label
                    }
                  </div>

                  <button
                    className={styles.mobileDrawerClose}
                    onClick={() => setMobileDrawer(null)}
                  >
                    <i className="lni lni-xmark" />
                  </button>
                </div>

                <div className={styles.mobileDrawerBody}>
                  {activeMobileChildren.map((sub) => (
                    <button
                      key={sub.key}
                      className={styles.mobileDrawerItem}
                      onClick={() => {
                        navigate(sub.route);
                        setMobileDrawer(null);
                      }}
                    >
                      <i
                        className={
                          sub.icon ||
                          "lni lni-chevron-right"
                        }
                      />

                      <span>{sub.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

        {!isMobile && <FloatingChatLauncher />}
      </div>
    </AcademyProvider>
  );
}

const Menu = () => {
  return (
    <ChatProvider>
      <MenuContent />
    </ChatProvider>
  );
};

export default Menu;
